// PBR product page.
//
// Drives the pieces Horizon's machinery doesn't cover:
// - the Additional Details slide-in drawer
// - the Inquire modal (auto-reopens after a Shopify contact-form post so the
//   success/error message is visible)
// - the Added to Basket confirmation modal (opens after a successful add,
//   with a checkout CTA — most pieces are one-of-a-kind) and the matching
//   "In Your Basket" disabled button state once the cart holds all stock
// - the gallery lightbox (click any image for a fullscreen viewer with
//   prev/next + keyboard navigation; on touch: pinch / double-tap to zoom,
//   drag to pan while zoomed, swipe to change image)
// - native share with clipboard fallback
//
// The sidebar pin is pure CSS (position: sticky) — no JS involved.
// Add-to-cart, quantity constraints and cart events are Horizon's
// product-form-component / quantity-selector-component, loaded globally.

class PbrProduct extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('[ref="drawer"]');
    this.drawerOverlay = this.querySelector('[ref="drawerOverlay"]');

    this.inquireModal = this.querySelector('[ref="inquireModal"]');
    this.inquireOverlay = this.querySelector('[ref="inquireOverlay"]');

    this.addedModal = this.querySelector('[ref="addedModal"]');
    this.addedOverlay = this.querySelector('[ref="addedOverlay"]');

    this.querySelector('[ref="drawerOpen"]')?.addEventListener('click', () => this.openLayer(this.drawer, this.drawerOverlay));
    this.querySelector('[ref="drawerClose"]')?.addEventListener('click', () => this.closeLayers());
    this.querySelector('[ref="inquireOpen"]')?.addEventListener('click', () => this.openLayer(this.inquireModal, this.inquireOverlay));
    this.querySelector('[ref="inquireClose"]')?.addEventListener('click', () => this.closeLayers());
    this.querySelector('[ref="addedClose"]')?.addEventListener('click', () => this.closeLayers());
    this.querySelector('[ref="addedContinue"]')?.addEventListener('click', () => this.closeLayers());
    this.drawerOverlay?.addEventListener('click', () => this.closeLayers());
    this.inquireOverlay?.addEventListener('click', () => this.closeLayers());
    this.addedOverlay?.addEventListener('click', () => this.closeLayers());

    // Horizon's product-form-component dispatches this (bubbling) on every
    // add; listening on the section element scopes it to this product's form.
    this.onCartLinesUpdate = (event) => this.handleCartLinesUpdate(event);
    this.addEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);

    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.closeLayers();
      if (this.openedLayer === this.lightbox) {
        if (event.key === 'ArrowLeft') this.stepLightbox(-1);
        if (event.key === 'ArrowRight') this.stepLightbox(1);
      }
    };
    document.addEventListener('keydown', this.onKeydown);

    this.bindShare();
    this.bindLightbox();

    // A completed contact-form post re-renders the page with the status
    // message inside the (closed) modal — reopen it so the user sees it.
    if (this.querySelector('[data-pbr-inquire-posted]')) {
      this.openLayer(this.inquireModal, this.inquireOverlay);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
  }

  /* ---- Add-to-basket confirmation ---- */
  handleCartLinesUpdate(event) {
    const promise = event.promise;
    if (!promise?.then) return;

    // The event fires the moment the form submits, before the request goes
    // out — show a pending state right away so the click never feels
    // ignored while Shopify processes the add.
    const button = this.querySelector('product-form-component [ref="addToCartButton"]');
    const label = button?.querySelector('.add-to-cart-text__content span span');
    const idleLabel = label?.textContent;
    if (button) button.dataset.pbrPending = 'true';
    if (label) label.textContent = 'Adding…';
    const settle = () => {
      if (button) delete button.dataset.pbrPending;
      if (label && label.textContent === 'Adding…' && idleLabel) label.textContent = idleLabel;
    };

    promise
      .then(({ detail }) => {
        settle();
        if (detail?.didError) return;
        this.markBasketMaxed(detail?.items);
        this.openLayer(this.addedModal, this.addedOverlay);
      })
      .catch(settle);
  }

  // Once the cart holds every unit we stock, turn the button into a
  // disabled "In Your Basket" state so a second add can't be attempted.
  markBasketMaxed(items) {
    const form = this.querySelector('product-form-component');
    const max = Number(form?.dataset.pbrMaxQty);
    if (!form || !max) return;

    const variantId = form.querySelector('input[name="id"]')?.value;
    let inCart = 0;
    if (Array.isArray(items)) {
      inCart = items
        .filter((item) => String(item.variant_id) === String(variantId))
        .reduce((sum, item) => sum + item.quantity, 0);
    } else if (max === 1) {
      // No cart payload on the event — but with a single unit in stock any
      // successful add means it's now maxed.
      inCart = 1;
    }
    if (inCart < max) return;

    const button = form.querySelector('[ref="addToCartButton"]');
    if (button) {
      button.disabled = true;
      const label = button.querySelector('.add-to-cart-text__content span span');
      if (label) label.textContent = 'In Your Basket';
    }
    form.querySelector('.quantity-selector-wrapper')?.setAttribute('hidden', '');
  }

  /* ---- Drawer / modal / lightbox open-close ---- */
  openLayer(layer, overlay) {
    if (!layer) return;
    this.closeLayers();
    layer.hidden = false;
    if (overlay) overlay.hidden = false;
    requestAnimationFrame(() => {
      layer.classList.add('is-open');
      overlay?.classList.add('is-open');
    });
    document.documentElement.style.overflow = 'hidden';
    this.openedLayer = layer;
    this.openedOverlay = overlay;
    this.lastFocus = document.activeElement;
    layer.querySelector('button, a, input, textarea')?.focus();
  }

  closeLayers() {
    const layer = this.openedLayer;
    const overlay = this.openedOverlay;
    if (!layer) return;
    if (layer === this.lightbox) this.resetLightboxZoom(false);
    layer.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    setTimeout(() => {
      if (!layer.classList.contains('is-open')) {
        layer.hidden = true;
        if (overlay) overlay.hidden = true;
      }
    }, 320);
    this.openedLayer = null;
    this.openedOverlay = null;
    if (this.lastFocus instanceof HTMLElement) this.lastFocus.focus();
  }

  /* ---- Lightbox ---- */
  bindLightbox() {
    this.lightbox = this.querySelector('[ref="lightbox"]');
    this.lightboxImage = this.querySelector('[ref="lightboxImage"]');
    this.lightboxCounter = this.querySelector('[ref="lightboxCounter"]');
    const prev = this.querySelector('[ref="lightboxPrev"]');
    const next = this.querySelector('[ref="lightboxNext"]');
    this.zoomButtons = Array.from(this.querySelectorAll('.pbr-product__zoom'));
    if (!this.lightbox || !this.lightboxImage || this.zoomButtons.length === 0) return;

    this.zoomButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        this.lightboxIndex = index;
        this.renderLightbox();
        this.openLayer(this.lightbox);
      });
    });

    prev?.addEventListener('click', () => this.stepLightbox(-1));
    next?.addEventListener('click', () => this.stepLightbox(1));
    if (this.zoomButtons.length < 2) {
      if (prev) prev.hidden = true;
      if (next) next.hidden = true;
    }

    this.querySelector('[ref="lightboxClose"]')?.addEventListener('click', () => this.closeLayers());
    this.bindLightboxGestures();
  }

  stepLightbox(direction) {
    const count = this.zoomButtons.length;
    if (!count) return;
    this.lightboxIndex = (this.lightboxIndex + direction + count) % count;
    this.renderLightbox();
  }

  // The grid image is already decoded, so it goes up instantly; the
  // full-res file replaces it once it has decoded off-screen (no flash of
  // the previous photo, no blank frame). Neighbours preload in the
  // background so the next swipe is usually instant.
  renderLightbox() {
    const button = this.zoomButtons[this.lightboxIndex];
    if (!button) return;
    this.resetLightboxZoom(false);
    const index = this.lightboxIndex;
    const image = this.lightboxImage;
    const spinner = this.querySelector('[ref="lightboxSpinner"]');
    const gridImage = button.parentElement?.querySelector('img');
    const alt = button.dataset.zoomAlt || '';

    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = `${index + 1} / ${this.zoomButtons.length}`;
    }

    const full = this.preloadZoom(button);
    image.alt = alt;
    if (full.complete && full.naturalWidth > 0) {
      this.showZoom(image, full);
      spinner?.setAttribute('hidden', '');
    } else {
      // Placeholder: whatever the grid already has on screen.
      if (gridImage?.currentSrc) {
        image.removeAttribute('srcset');
        image.src = gridImage.currentSrc;
      }
      spinner?.removeAttribute('hidden');
      (full.decode ? full.decode() : Promise.resolve()).catch(() => {}).then(() => {
        if (this.lightboxIndex !== index) return;
        this.showZoom(image, full);
        spinner?.setAttribute('hidden', '');
      });
    }

    const count = this.zoomButtons.length;
    if (count > 1) {
      this.preloadZoom(this.zoomButtons[(index + 1) % count]);
      this.preloadZoom(this.zoomButtons[(index - 1 + count) % count]);
    }
  }

  showZoom(image, full) {
    if (full.srcset) image.srcset = full.srcset;
    image.src = full.src;
  }

  preloadZoom(button) {
    this.zoomCache ||= new Map();
    let img = this.zoomCache.get(button);
    if (img) return img;
    img = new Image();
    img.sizes = '100vw';
    if (button.dataset.zoomSrcset) img.srcset = button.dataset.zoomSrcset;
    img.src = button.dataset.zoomSrc;
    this.zoomCache.set(button, img);
    return img;
  }

  /* ---- Lightbox gestures ----
     One pointer at scale 1: horizontal swipe steps prev/next (the image
     follows the finger, then snaps). One pointer while zoomed: pan.
     Two pointers: pinch-zoom anchored on the midpoint. Double-tap toggles
     between fit and 2.5x at the tapped spot. A plain tap on the backdrop
     (outside the photo) closes. Mouse works the same way (drag / dblclick). */
  bindLightboxGestures() {
    this.lightboxStage = this.querySelector('[ref="lightboxStage"]');
    this.lightboxHint = this.querySelector('[ref="lightboxHint"]');
    const stage = this.lightboxStage;
    const image = this.lightboxImage;
    if (!stage || !image || !window.PointerEvent) return;

    this.lb = { scale: 1, x: 0, y: 0, min: 1, max: 4, pointers: new Map(), lastTap: 0 };

    const setTransform = (settle) => {
      image.classList.toggle('is-settling', Boolean(settle));
      image.style.setProperty('--lb-scale', String(this.lb.scale));
      image.style.setProperty('--lb-x', `${this.lb.x}px`);
      image.style.setProperty('--lb-y', `${this.lb.y}px`);
      stage.classList.toggle('is-zoomed', this.lb.scale > 1.01);
    };
    this.applyLightboxTransform = setTransform;

    // Keep the zoomed photo covering the stage: no drifting past its edges.
    const clamp = () => {
      // offsetWidth/Height are the layout size, untouched by the transform.
      const stageRect = stage.getBoundingClientRect();
      const maxX = Math.max(0, (image.offsetWidth * this.lb.scale - stageRect.width) / 2);
      const maxY = Math.max(0, (image.offsetHeight * this.lb.scale - stageRect.height) / 2);
      this.lb.x = Math.min(maxX, Math.max(-maxX, this.lb.x));
      this.lb.y = Math.min(maxY, Math.max(-maxY, this.lb.y));
    };

    // Point (in stage-centred coords) → scale about it, keeping it fixed.
    const zoomAt = (nextScale, px, py) => {
      const prev = this.lb.scale;
      const scale = Math.min(this.lb.max, Math.max(this.lb.min, nextScale));
      const qx = (px - this.lb.x) / prev;
      const qy = (py - this.lb.y) / prev;
      this.lb.scale = scale;
      this.lb.x = px - qx * scale;
      this.lb.y = py - qy * scale;
      if (scale <= 1.001) { this.lb.scale = 1; this.lb.x = 0; this.lb.y = 0; }
      clamp();
    };

    const stagePoint = (event) => {
      const r = stage.getBoundingClientRect();
      return { x: event.clientX - (r.left + r.width / 2), y: event.clientY - (r.top + r.height / 2) };
    };

    let gesture = null;

    stage.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      this.lb.pointers.set(event.pointerId, stagePoint(event));
      try { stage.setPointerCapture(event.pointerId); } catch (error) { /* synthetic / already-released pointer */ }
      image.classList.remove('is-settling');

      const pts = Array.from(this.lb.pointers.values());
      if (pts.length === 1) {
        gesture = {
          type: this.lb.scale > 1 ? 'pan' : 'swipe',
          start: pts[0],
          startX: this.lb.x,
          startY: this.lb.y,
          time: performance.now(),
          moved: false,
          onImage: event.target === image,
        };
      } else if (pts.length === 2) {
        const [a, b] = pts;
        gesture = {
          type: 'pinch',
          startDist: Math.hypot(a.x - b.x, a.y - b.y),
          startScale: this.lb.scale,
          // Image point under the initial midpoint, in unscaled coords.
          qx: ((a.x + b.x) / 2 - this.lb.x) / this.lb.scale,
          qy: ((a.y + b.y) / 2 - this.lb.y) / this.lb.scale,
          moved: true,
        };
        this.hideLightboxHint();
      }
      event.preventDefault();
    });

    stage.addEventListener('pointermove', (event) => {
      if (!this.lb.pointers.has(event.pointerId) || !gesture) return;
      this.lb.pointers.set(event.pointerId, stagePoint(event));
      const pts = Array.from(this.lb.pointers.values());

      if (gesture.type === 'pinch' && pts.length >= 2) {
        const [a, b] = pts;
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const scale = Math.min(this.lb.max, Math.max(this.lb.min, gesture.startScale * (dist / gesture.startDist)));
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        this.lb.scale = scale;
        this.lb.x = mx - gesture.qx * scale;
        this.lb.y = my - gesture.qy * scale;
        clamp();
        setTransform(false);
        return;
      }

      if (pts.length !== 1) return;
      const dx = pts[0].x - gesture.start.x;
      const dy = pts[0].y - gesture.start.y;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) gesture.moved = true;

      if (gesture.type === 'pan') {
        this.lb.x = gesture.startX + dx;
        this.lb.y = gesture.startY + dy;
        clamp();
        setTransform(false);
      } else if (gesture.type === 'swipe' && gesture.moved) {
        // Follow the finger horizontally so the swipe feels physical.
        this.lb.x = dx;
        this.lb.y = 0;
        setTransform(false);
      }
    });

    const endPointer = (event) => {
      if (!this.lb.pointers.has(event.pointerId)) return;
      const point = this.lb.pointers.get(event.pointerId);
      this.lb.pointers.delete(event.pointerId);
      try { stage.releasePointerCapture(event.pointerId); } catch (error) { /* not captured */ }
      const current = gesture;

      if (this.lb.pointers.size > 0) {
        // Pinch ending with one finger still down: hand off to a pan/swipe
        // from the remaining finger's position so nothing jumps.
        const remaining = Array.from(this.lb.pointers.values())[0];
        gesture = {
          type: this.lb.scale > 1 ? 'pan' : 'swipe',
          start: remaining,
          startX: this.lb.x,
          startY: this.lb.y,
          time: performance.now(),
          moved: true,
          onImage: false,
        };
        if (this.lb.scale <= 1) { this.lb.x = 0; this.lb.y = 0; setTransform(true); }
        return;
      }
      gesture = null;
      if (!current) return;

      if (current.type === 'pinch') {
        if (this.lb.scale <= 1.001) this.resetLightboxZoom(true);
        else setTransform(true);
        return;
      }

      if (current.type === 'swipe') {
        const dx = point.x - current.start.x;
        const dy = point.y - current.start.y;
        const elapsed = performance.now() - current.time;
        const stageWidth = stage.getBoundingClientRect().width;
        const farEnough = Math.abs(dx) > Math.min(80, stageWidth * 0.2);
        const flick = Math.abs(dx) > 30 && elapsed < 300;
        if (current.moved && Math.abs(dx) > Math.abs(dy) && (farEnough || flick) && this.zoomButtons.length > 1) {
          this.stepLightbox(dx < 0 ? 1 : -1);
          return;
        }
        if (current.moved) {
          this.lb.x = 0;
          setTransform(true);
          return;
        }
      }

      if (current.type === 'pan' && current.moved) {
        setTransform(true);
        return;
      }

      // No movement: it's a tap. Double-tap toggles zoom; a lone tap on the
      // backdrop closes; a lone tap on the photo does nothing.
      const now = performance.now();
      const isDouble = now - this.lb.lastTap < 320;
      this.lb.lastTap = isDouble ? 0 : now;
      if (isDouble) {
        clearTimeout(this.lb.tapTimer);
        if (this.lb.scale > 1) this.resetLightboxZoom(true);
        else { zoomAt(2.5, point.x, point.y); setTransform(true); }
        this.hideLightboxHint();
      } else if (!current.onImage) {
        clearTimeout(this.lb.tapTimer);
        this.lb.tapTimer = setTimeout(() => this.closeLayers(), 330);
      }
    };
    stage.addEventListener('pointerup', endPointer);
    stage.addEventListener('pointercancel', endPointer);

    // Desktop: scroll wheel / trackpad pinch zooms about the cursor.
    stage.addEventListener('wheel', (event) => {
      if (!event.ctrlKey && Math.abs(event.deltaY) < 1) return;
      event.preventDefault();
      const point = stagePoint(event);
      const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.002));
      zoomAt(this.lb.scale * factor, point.x, point.y);
      setTransform(false);
    }, { passive: false });

    setTransform(false);
  }

  resetLightboxZoom(settle) {
    if (!this.lb) return;
    this.lb.scale = 1;
    this.lb.x = 0;
    this.lb.y = 0;
    this.applyLightboxTransform?.(settle);
  }

  hideLightboxHint() {
    if (this.lightboxHint && !this.lightboxHint.hidden) this.lightboxHint.hidden = true;
  }

  /* ---- Share ---- */
  bindShare() {
    const button = this.querySelector('[ref="shareButton"]');
    if (!button) return;
    button.addEventListener('click', async () => {
      const url = button.dataset.shareUrl || window.location.href;
      const title = button.dataset.shareTitle || document.title;
      if (navigator.share) {
        try {
          await navigator.share({ title, url });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        const label = button.querySelector('span');
        if (label) {
          const original = label.textContent;
          label.textContent = 'Copied!';
          setTimeout(() => (label.textContent = original), 1600);
        }
      } catch (error) {
        // Clipboard unavailable — nothing sensible left to do.
      }
    });
  }
}

if (!customElements.get('pbr-product')) {
  customElements.define('pbr-product', PbrProduct);
}
